import axiosInstance from "@/api/axiosInstance";
import { ENDPOINTS } from "@/constants/api";
import { requestHandler } from "@/helpers/requestHandler";

export interface Advertisement {
  id: number;
  adid: number;
  type: 'ad_photo' | 'ad_vid';
  resource: string;
  shareurl: string;
  buttontext: string;
  buttonurl: string;
  adtext: string;
  qualifyingquery: string;
  profilepage: number | null;
  sessionpage: number | null;
  profilelistposition: number | null;
  sessionlistposition: number | null;
  active: boolean;
  internalurl: boolean;
}

export interface AdvertisementRequestDTO {
  adid: number;
  type: 'ad_photo' | 'ad_vid';
  resource: string;
  shareurl: string;
  buttontext: string;
  buttonurl: string;
  adtext: string;
  qualifyingquery: string;
  profilepage: number | null;
  sessionpage: number | null;
  profilelistposition: number | null;
  sessionlistposition: number | null;
  active: boolean;
  internalurl: boolean;
}

export interface AdvertisementPaginatedResponse {
  data: Advertisement[];
  total: number;
  totalPages: number;
  currentPage: number;
}

export interface AdvertisementFilters {
  active?: boolean | null;
  type?: 'ad_photo' | 'ad_vid' | null;
  page?: 'profile' | 'session' | 'both' | null;
}

export const AdvertisementService = {
  async getAdvertisements(
    limit: number,
    sortField: string,
    sortOrder: "ASC" | "DESC",
    page: number,
    search?: string,
    filters?: AdvertisementFilters
  ): Promise<AdvertisementPaginatedResponse> {
    return requestHandler<AdvertisementPaginatedResponse>(async () => {
      const params = new URLSearchParams();
      params.append("limit", limit.toString());
      // Backend expects a zero-based page number and computes the offset itself
      params.append("page", (page - 1).toString());

      if (sortField) {
        params.append("sortField", sortField);
        params.append("sortOrder", sortOrder);
      }

      if (search) {
        params.append("searchval", search);
      }

      // Add filter parameters
      if (filters?.active !== null && filters?.active !== undefined) {
        params.append("active", filters.active.toString());
      }

      if (filters?.type) {
        params.append("type", filters.type);
      }

      if (filters?.page) {
        params.append("pageFilter", filters.page);
      }

      const response = await axiosInstance.get(
        `${ENDPOINTS.ADVERTISEMENTS}?${params.toString()}`
      );

      return response.data;
    });
  },

  async createAdvertisement(data: AdvertisementRequestDTO): Promise<Advertisement> {
    const response = await axiosInstance.post<Advertisement>("/advertisements", data);
    return response.data;
  },

  async updateAdvertisement(id: number, data: AdvertisementRequestDTO): Promise<Advertisement> {
    const response = await axiosInstance.put<Advertisement>(`/advertisements/${id}`, data);
    return response.data;
  },

  async deleteAdvertisement(id: number): Promise<void> {
    await axiosInstance.delete(`/advertisements/${id}`);
  },

  async toggleAdvertisementStatus(id: number, active: boolean): Promise<Advertisement> {
    const response = await axiosInstance.patch<Advertisement>(`/advertisements/${id}/status`, {
      active
    });
    return response.data;
  },

  async duplicateAdvertisement(id: number): Promise<Advertisement> {
    const response = await axiosInstance.post<Advertisement>(`/advertisements/${id}/duplicate`);
    return response.data;
  },

  async testQualifyingQuery(
    query: string,
    userId?: number
  ): Promise<{ valid: boolean; result: any; message?: string; hasResults?: boolean; error?: string }> {
    const response = await axiosInstance.post("/advertisements/test-query", {
      query,
      userId: userId || 1,
    });
    return response.data;
  },

  async previewQualifyingQuery(
    query: string,
    sampleUserAgent: string,
    userId?: number
  ): Promise<{ valid: boolean; result: any; substitutedQuery?: string; message?: string; hasResults?: boolean; error?: string }> {
    const response = await axiosInstance.post("/advertisements/test-query/preview", {
      query,
      userId: userId || 1,
      sampleUserAgent,
    });
    return response.data;
  },

  async uploadAndOptimizeImage(file: File, crop?: { x: number, y: number, width: number, height: number }): Promise<string> {
    const formData = new FormData();
    formData.append('image', file);

    // Add optimization parameters - fixed 16:9 aspect ratio
    formData.append('maxWidth', '1280');  // Fixed width for 16:9
    formData.append('maxHeight', '720');  // Fixed height for 16:9
    formData.append('quality', '85');     // PNG/JPEG quality (1-100)
    formData.append('format', 'png');     // Output format

    // Add crop coordinates if provided
    if (crop) {
      formData.append('cropX', Math.round(crop.x).toString());
      formData.append('cropY', Math.round(crop.y).toString());
      formData.append('cropWidth', Math.round(crop.width).toString());
      formData.append('cropHeight', Math.round(crop.height).toString());
    }

    const response = await axiosInstance.post<{ url: string }>('/advertisements/upload-image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data.url;
  }
};